import {
  CustomerServiceOutlined,
  FacebookOutlined,
  InstagramOutlined,
  QuestionCircleOutlined,
  ShopOutlined,
  ShoppingOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { Layout } from 'antd';
import { APP_NAME } from 'config/constants/api.constants';
import { RouteName } from 'config/constants/route.constants';
import { FooterTranscription, UserTranscription } from 'config/constants/transcription.constant';
import GlobalUtils from 'config/utils/globals.util';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from 'store/index';
import { PaymentMethod } from 'store/models/order.model';
import styles from './style.module.scss';

type FooterShortcut = {
  key: string;
  icon: React.ReactNode;
  title: string;
  to?: string;
  href?: string;
};

type FooterLink = {
  label: string;
  to?: string;
  upcoming?: boolean;
};

const shortcuts: FooterShortcut[] = [
  {
    key: 'help',
    icon: <QuestionCircleOutlined />,
    title: FooterTranscription.HELP_CENTER,
    href: '#footer-help',
  },
  {
    key: 'purchases',
    icon: <ShoppingOutlined />,
    title: FooterTranscription.ORDER_TRACKING,
    to: RouteName.MY_PURCHASES,
  },
  {
    key: 'pickup',
    icon: <ShopOutlined />,
    title: FooterTranscription.PICKUP,
    href: '#footer-help',
  },
  {
    key: 'contact',
    icon: <CustomerServiceOutlined />,
    title: FooterTranscription.CONTACT,
    href: '#footer-social',
  },
];

const helpLinks: FooterLink[] = [
  { label: FooterTranscription.HOW_TO_BUY, to: RouteName.HOME },
  { label: FooterTranscription.SHIPPING_AND_PICKUP, upcoming: true },
  { label: FooterTranscription.PAYMENT_METHODS, upcoming: true },
  { label: FooterTranscription.CONTACT, upcoming: true },
];

const accountLinks: FooterLink[] = [
  { label: UserTranscription.USER_MY_PROFILE, to: RouteName.MY_PROFILE },
  { label: UserTranscription.USER_MY_PURCHASES, to: RouteName.MY_PURCHASES },
  { label: UserTranscription.USER_MY_FAVORITES, to: RouteName.MY_FAVORITES },
];

const storeLinks: FooterLink[] = [
  { label: FooterTranscription.ABOUT_US, upcoming: true },
  { label: FooterTranscription.TERMS, upcoming: true },
  { label: FooterTranscription.PRIVACY, upcoming: true },
];

const renderFooterLink = (item: FooterLink): React.ReactNode => {
  if (item.to) {
    return (
      <Link className={styles['link']} to={item.to}>
        {item.label}
      </Link>
    );
  }

  return (
    <span className={styles['mutedLink']} title={FooterTranscription.COMING_SOON}>
      {item.label}
    </span>
  );
};

const renderShortcut = (shortcut: FooterShortcut): React.ReactNode => {
  const content = (
    <React.Fragment>
      <span className={styles['shortcutIcon']}>{shortcut.icon}</span>
      <span className={styles['shortcutTitle']}>{shortcut.title}</span>
    </React.Fragment>
  );

  if (shortcut.to) {
    return (
      <Link key={shortcut.key} className={styles['shortcut']} to={shortcut.to}>
        {content}
      </Link>
    );
  }

  return (
    <a key={shortcut.key} className={styles['shortcut']} href={shortcut.href}>
      {content}
    </a>
  );
};

const renderPaymentMethod = (method: PaymentMethod): React.ReactNode => {
  if (method.logoUrl) {
    return <img key={method.slug} className={styles['paymentLogo']} src={method.logoUrl} alt={method.name} title={method.name} />;
  }

  return (
    <span key={method.slug} className={styles['paymentBadge']}>
      {method.name}
    </span>
  );
};

const Footer: React.FC = () => {
  const paymentMethods = useSelector((state: RootState) => state.order.paymentMethods ?? []);
  const activePaymentMethods = paymentMethods.filter((method) => method.isActive);
  return (
    <Layout.Footer className={styles['footer']}>
      <div className={styles['shortcutBar']}>
        <div className={styles['inner']}>
          <nav className={styles['shortcuts']} aria-label={FooterTranscription.HELP}>
            {shortcuts.map(renderShortcut)}
          </nav>
        </div>
      </div>
      <div className={styles['inner']}>
        <div className={styles['columns']}>
          <section id='footer-help'>
            <h3 className={styles['columnTitle']}>{FooterTranscription.HELP}</h3>
            <ul className={styles['linkList']}>
              {helpLinks.map((item) => (
                <li key={item.label}>{renderFooterLink(item)}</li>
              ))}
            </ul>
          </section>
          <section>
            <h3 className={styles['columnTitle']}>{FooterTranscription.MY_ACCOUNT}</h3>
            <ul className={styles['linkList']}>
              {accountLinks.map((item) => (
                <li key={item.label}>{renderFooterLink(item)}</li>
              ))}
            </ul>
          </section>
          <section>
            <h3 className={styles['columnTitle']}>{FooterTranscription.ABOUT_STORE}</h3>
            <ul className={styles['linkList']}>
              {storeLinks.map((item) => (
                <li key={item.label}>{renderFooterLink(item)}</li>
              ))}
            </ul>
          </section>
          <section id='footer-social'>
            <h3 className={styles['columnTitle']}>{FooterTranscription.FOLLOW_US}</h3>
            <div className={styles['social']}>
              <span className={styles['socialItem']} title={FooterTranscription.COMING_SOON} aria-label='Instagram'>
                <InstagramOutlined />
              </span>
              <span className={styles['socialItem']} title={FooterTranscription.COMING_SOON} aria-label='Facebook'>
                <FacebookOutlined />
              </span>
              <span className={styles['socialItem']} title={FooterTranscription.COMING_SOON} aria-label='WhatsApp'>
                <WhatsAppOutlined />
              </span>
            </div>
          </section>
        </div>
      </div>
      <div className={styles['bottom']}>
        <div className={styles['bottomInner']}>
          {activePaymentMethods.length > 0 && (
            <div className={styles['payments']}>
              <span className={styles['paymentLabel']}>{FooterTranscription.PAYMENT_METHODS}</span>
              {activePaymentMethods.map(renderPaymentMethod)}
            </div>
          )}
          <div className={styles['legal']}>
            <span>
              {APP_NAME} {GlobalUtils.getFullYearAndVersion()}
            </span>
            <span>{FooterTranscription.ALL_RIGHTS_RESERVED}</span>
            <span title={FooterTranscription.COMING_SOON}>{FooterTranscription.TERMS}</span>
            <span title={FooterTranscription.COMING_SOON}>{FooterTranscription.PRIVACY}</span>
          </div>
        </div>
      </div>
    </Layout.Footer>
  );
};

export default Footer;
